/**
 * Utility functions for product-related operations
 */

/**
 * Checks if a string is a UUID
 * @param str - String to check
 * @returns boolean indicating if the string is a UUID
 */
export const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Gets a display name for a product, handling cases where the name might be a UUID
 * @param product - Product object
 * @param fallbackPrefix - Prefix to use for fallback names (default: "Product")
 * @returns A user-friendly display name
 */
export const getProductDisplayName = (product: any, fallbackPrefix: string = "Product"): string => {
  // If product name exists and is not a UUID, use it
  if (product.name && !isUUID(product.name)) {
    return product.name;
  }
  
  // If product has a label and it's not a UUID, use it
  if (product.label && !isUUID(product.label)) {
    return product.label;
  }
  
  // If product has a description and it's not a UUID, use it
  if (product.description && !isUUID(product.description)) {
    return product.description;
  }
  
  // Use product type as fallback
  if (product.type) {
    return `${product.type.charAt(0).toUpperCase() + product.type.slice(1)} Product`;
  }
  
  // Use product ID last 4 characters as final fallback
  if (product.id) {
    return `${fallbackPrefix} ${product.id.slice(-4)}`;
  }
  
  // Ultimate fallback
  return fallbackPrefix;
};

/**
 * Gets a short display name for a product (for compact views)
 * @param product - Product object
 * @returns A short user-friendly display name
 */
export const getProductShortName = (product: any): string => {
  const fullName = getProductDisplayName(product);
  
  // If it's a long name, truncate it
  if (fullName.length > 20) {
    return fullName.substring(0, 17) + '...';
  }
  
  return fullName;
};
